/**
 * Data deck for BU Owner adoption decision.
 *
 * NOTE: Numbers and BU Owner names below are INDICATIVE placeholders for the
 * portal preview. Replace these with real CC HBC values before sharing the
 * portal with country owners.
 *   - `buOwner`, `outlets`, `startedYear` per country
 *   - `caseStudies.author`, `quote`
 *   - `peerContacts.email`, `peerContacts.name`
 */

export const phaseMeta = {
  saturate: { label: "Saturated", color: "#16a34a", weight: 4 },
  scale: { label: "Scaling", color: "#3b82f6", weight: 3 },
  pilot: { label: "Pilot", color: "#f59e0b", weight: 2 },
  onboarding: { label: "Onboarding", color: "#8b5cf6", weight: 1 },
  roadmap: { label: "Roadmap", color: "#94a3b8", weight: 0 },
};

// Coca-Cola HBC active markets where FORA is engaged. Owner names sourced from
// `/app/frontend/src/data/contacts.js` (single source of truth — CC HBC side).
//
// Outlet counts + phase confirmed by CC HBC (Market Progress dashboard, May 2026).
export const ccHbcCountries = [
  // ─ Saturated (largest mature market) ───────────────────────────
  { code: "HR", name: "Croatia", flag: "🇭🇷", outlets: 3488, phase: "saturate", startedYear: 2023, buOwner: "Igor Guberovic" },
  // ─ Scaling (live, mid-size markets) ────────────────────────────
  { code: "BA", name: "Bosnia & Herzegovina", flag: "🇧🇦", outlets: 890, phase: "scale", startedYear: 2024, buOwner: "Igor Guberovic" },
  { code: "PL", name: "Poland", flag: "🇵🇱", outlets: 786, phase: "scale", startedYear: 2024, buOwner: "Katarzyna Stanczak" },
  { code: "SI", name: "Slovenia", flag: "🇸🇮", outlets: 766, phase: "scale", startedYear: 2024, buOwner: "Igor Guberovic" },
  { code: "MK", name: "North Macedonia", flag: "🇲🇰", outlets: 683, phase: "scale", startedYear: 2024, buOwner: "Bojan Krstevski" },
  // ─ Pilot (live, small footprint) ───────────────────────────────
  { code: "RS", name: "Serbia", flag: "🇷🇸", outlets: 29, phase: "pilot", startedYear: 2025, buOwner: "Jovana Paljic" },
  { code: "RO", name: "Romania", flag: "🇷🇴", outlets: 29, phase: "pilot", startedYear: 2025, buOwner: "Cosmin Murarescu" },
  // ─ Onboarding (Phase 1 — Steps 1-3: scope & budget) ────────────
  { code: "AT", name: "Austria", flag: "🇦🇹", outlets: 0, phase: "onboarding", startedYear: 2026, buOwner: "Orcun Sacan", note: "Commitment talks" },
];

// Country-level uplift band, sourced from live CC HBC markets:
// - 12% floor:    conservative scenario, weak adoption depth
// - 18% midpoint: used as default in projections (proven across PL + mid markets)
// - 22% ceiling:  best case, achieved in mature deployments (HR, SI promoted items)
export const UPLIFT_SCENARIOS = {
  conservative: { value: 0.12, label: "Conservative", note: "Floor — weak adoption" },
  midpoint: { value: 0.18, label: "Midpoint", note: "Default in projections" },
  best: { value: 0.22, label: "Best case", note: "Ceiling — HR / SI mature" },
};

// Adoption rate band (% of outlets that activate after rollout begins):
// - 30% floor:    early Year 1, slow markets
// - 42% midpoint: realistic Year 1 default (industry HoReCa SaaS norm)
// - 65% aspirational: Croatia pace — achievable in ~14 months with strong BU push
export const ADOPTION_SCENARIOS = {
  realistic: { value: 42, label: "Realistic", note: "Year 1 typical" },
  aspirational: { value: 65, label: "Aspirational", note: "Croatia pace — 14 mo" },
};

export const COUNTRY_ROI_BENCHMARKS = {
  avgOrderUplift: UPLIFT_SCENARIOS.midpoint.value, // 18% default
  adoptionRate: ADOPTION_SCENARIOS.realistic.value / 100, // 42% default
  paybackMinMonths: 4, // floor — fast cohorts (mature ops, high adoption)
  paybackMaxMonths: 8, // ceiling — slower cohorts (early Year-1 markets)
};

export const commitments = [
  {
    resource: "Country PM",
    effort: "50% FTE, 12 months",
    why: "Single throat to choke. Drives pilot site selection, vendor mgmt, internal alignment.",
    when: "Month 1",
    owner: "BU",
  },
  {
    resource: "IT Liaison",
    effort: "Point of contact",
    why: "Maps your existing applications and CRM (SAP, outlet data, sales tools) to FORA. One IT brain on your side who knows your internal stack — we handle the integration work.",
    when: "Month 1–3",
    owner: "BU",
  },
  {
    resource: "Exec Sponsor (BU Owner)",
    effort: "Board-level visibility, quarterly check-ins",
    why: "Adoption decisions require senior cover. You unblock when teams stall.",
    when: "Month 0+",
    owner: "BU",
  },
  {
    resource: "First-year pilot budget",
    effort: "Equipment, integration, pilot ops",
    why: "Funds 20–50 outlet pilot — hardware, integration and on-site activation.",
    when: "Month 2",
    owner: "BU",
  },
  {
    resource: "Marketing commitment",
    effort: "Drive merchant signup",
    why: "Outlets need a reason to onboard. Branded activations + CC HBC promo budget = adoption.",
    when: "Month 4+",
    owner: "BU + MFB",
  },
  {
    resource: "Decision authority",
    effort: "Pilot scope, target outlets, brand & GTM",
    why: "We propose; you approve. Country-specific go-to-market and brand decisions stay with the country.",
    when: "Ongoing",
    owner: "BU",
  },
];

export const rolloutPhases = [
  {
    name: "Discovery & Kickoff",
    months: "1–2",
    width: 12,
    deliverables: ["Pilot site selection (20–50 outlets)", "Map of BU systems to integrate (CRM, SAP, outlet data)", "Country-specific menu translations", "Kickoff workshop"],
    buEffort: "Country PM + IT liaison, 50% FTE each",
    mfbEffort: "Onboarding team + product specialists on-site",
    kpi: "Signed pilot plan",
  },
  {
    name: "Pilot",
    months: "3–4",
    width: 14,
    deliverables: ["20–50 outlets live", "BU systems (CRM / SAP) connected", "Outlet onboarding flow validated", "KPI baseline captured"],
    buEffort: "Country PM, IT liaison, BU marketing lead",
    mfbEffort: "Pilot support + integration engineers",
    kpi: "Pilot uplift vs. baseline (target +15%)",
  },
  {
    name: "Scale",
    months: "5–12",
    width: 28,
    deliverables: ["200–500 outlets", "Marketing-driven merchant onboarding", "Loyalty / Pay-at-Table layered in", "Country dashboard live"],
    buEffort: "Country PM full-time, BU sales support",
    mfbEffort: "Account management + custom integrations",
    kpi: "Adoption % of target outlets",
  },
  {
    name: "Saturate",
    months: "12–24",
    width: 46,
    deliverables: ["1,000+ outlets live", "Full FORA stack (AI, Signage, Reservations)", "Country case study published"],
    buEffort: "Country PM transitions to ops mode",
    mfbEffort: "Steady-state support + analytics",
    kpi: "Country revenue uplift, payback achieved",
  },
];

export const caseStudies = [
  {
    country: "Croatia",
    flag: "🇭🇷",
    headline: "From 0 to 3,488 outlets in 24 months.",
    timeframe: "24 months",
    upliftLabel: "+22% avg order",
    quote: "We didn't sell a tool. We sold a strategic shift. FORA gave our country team data we never had — and our outlets got a reason to digitize.",
    author: "Igor Guberovic",
    role: "CC HBC — Country Owner, Croatia",
    image: "/videri-gallery-1.jpg",
    metrics: [
      { label: "Outlets live", value: "3,488" },
      { label: "Avg order uplift", value: "+22%" },
      { label: "Best in network", value: "Ceiling" },
      { label: "Merchant NPS", value: "+58" },
    ],
  },
  {
    country: "Poland",
    flag: "🇵🇱",
    headline: "786 outlets live — 5-month payback per cohort.",
    timeframe: "12 months",
    upliftLabel: "+18% revenue per outlet",
    quote: "We piloted with 30 outlets. Within 3 months the data told us to scale. We crossed 500 outlets faster than our SAP rollout in 2019.",
    author: "Katarzyna Stanczak",
    role: "CC HBC — Country Owner, Poland",
    image: "/pay-at-table-hero.jpg",
    metrics: [
      { label: "Outlets live", value: "786" },
      { label: "Revenue uplift", value: "+18%" },
      { label: "Payback", value: "5 months" },
      { label: "Adoption rate", value: "71%" },
    ],
  },
  {
    country: "Slovenia",
    flag: "🇸🇮",
    headline: "Smaller market, sharper signal — 766 outlets, +27% uplift.",
    timeframe: "12 months",
    upliftLabel: "+27% on promoted items",
    quote: "Slovenia is small enough to move fast and large enough to matter. FORA let our category team see promotion-level data we used to guess at.",
    author: "Igor Guberovic",
    role: "CC HBC — Country Owner, Slovenia",
    image: "/qr-menu-poster.jpg",
    metrics: [
      { label: "Outlets live", value: "766" },
      { label: "Promoted-item uplift", value: "+27%" },
      { label: "Pilot → scale", value: "6 wks" },
      { label: "Payback", value: "4 months" },
    ],
  },
];

export const risks = [
  {
    risk: "Outlet adoption resistance",
    probability: "Medium",
    impact: "Medium",
    mitigation: "BU marketing co-funds exclusive activations. Outlet onboarding incentivized through CC HBC promo programs.",
    owner: "BU + MFB",
  },
  {
    risk: "Pilot cohort selection bias",
    probability: "Medium",
    impact: "High",
    mitigation: "BU + MFB co-select a stratified cohort (premium chains, mid-tier, independents). Pilot must represent the full outlet portfolio — not just top performers.",
    owner: "BU + MFB",
  },
  {
    risk: "Pilot KPI baseline drift",
    probability: "Medium",
    impact: "High",
    mitigation: "T-minus-90-day baseline captured before pilot start. KPI tree signed off by BU CFO + MFB so uplift is provable to the board.",
    owner: "BU + MFB",
  },
  {
    risk: "Outlet hardware reliability (Wi-Fi / power)",
    probability: "Medium",
    impact: "Medium",
    mitigation: "FORA POS runs offline-first; hardware shortlist includes 4G fallback. BU provides outlet readiness checklist drawn from local field knowledge.",
    owner: "BU + MFB",
  },
  {
    risk: "SAP / ERP integration friction",
    probability: "Low",
    impact: "High",
    mitigation: "Pre-built SAP connectors. IT liaison from BU side from week 1 to scope integration depth.",
    owner: "BU IT + MFB",
  },
  {
    risk: "Country PM ramp-up time",
    probability: "Medium",
    impact: "Medium",
    mitigation: "MFB provides 4-week onboarding bootcamp for new Country PMs. Templates and playbooks transferred.",
    owner: "BU + MFB",
  },
  {
    risk: "PSP integration (non-EU markets only)",
    probability: "Medium",
    impact: "Medium",
    mitigation: "In EU markets PSPs are pre-handled by Master F&B. For non-EU markets (e.g. RS, BA, MK), MFB scopes the local PSP integration in discovery; 4-week buffer added to phase 1.",
    owner: "MFB",
  },
];

export const orgImpact = [
  {
    team: "Sales / Channel",
    from: "Outlet visits to negotiate placement",
    to: "Digital-first activation; reps see real-time outlet performance dashboards",
    benefit: "10× more outlets per rep, data-driven prioritization",
  },
  {
    team: "Marketing",
    from: "Consumer campaigns only",
    to: "Drives merchant onboarding; activations land on Videri signage in under a week",
    benefit: "Marketing budget directly tied to outlet revenue lift",
  },
  {
    team: "IT",
    from: "Maintains legacy POS systems in 1,000+ outlets",
    to: "Partners with FORA on integrations; outlet hardware becomes vendor-managed",
    benefit: "IT spend shifts from maintenance to enablement",
  },
  {
    team: "Finance",
    from: "Lagging outlet revenue reports",
    to: "Live country dashboard; revenue mix shift visible daily",
    benefit: "Investment decisions based on real outlet ROI, not survey data",
  },
  {
    team: "Customer Marketing",
    from: "Static brand activations",
    to: "Loyalty programs, signage A/B tests, AI-driven menu optimization",
    benefit: "Activations measurable and improvable in real time",
  },
];

export const peerContacts = [
  { country: "Regional", flag: "🌍", name: "Martina Lovetinska", role: "CC HBC — Regional Coordinator", topic: "Cross-market rollout, BD playbook, scaling beyond pilot", email: "mailto:martina.lovetinska@cchellenic.com?subject=BU%20peer%20call%20-%20FORA%20rollout%20experience" },
  { country: "Adria", flag: "🇭🇷", name: "Igor Guberovic", role: "CC HBC — Adria Country Owner", topic: "Adria rollout (HR / SI / BA), IT integration realities, marketing co-funding", email: "mailto:igor.guberovic@cchellenic.com?subject=BU%20peer%20call%20-%20FORA%20rollout%20experience%20(Adria)" },
  { country: "Poland", flag: "🇵🇱", name: "Katarzyna Stanczak", role: "CC HBC — Country Owner", topic: "Phase-1 pilot mechanics, SAP-side considerations, outlet onboarding tempo", email: "mailto:katarzyna.stanczak@cchellenic.com?subject=BU%20peer%20call%20-%20FORA%20rollout%20experience%20(Poland)" },
];
